import { corporatePages } from "./navigation.ts";

/**
 * Which page the address bar is asking for.
 *
 * The same mechanism the dispatch console uses, and for the same two reasons. "Come back to the
 * page you were on" needs the page written down somewhere that survives leaving for the identity
 * provider and returning, and a URL is that somewhere. It is also what makes a page shareable
 * between two administrators looking at the same change.
 *
 * A query parameter rather than a path, for one practical reason: a path needs the server to answer
 * every path with the application's document, and this console is served as static files from a
 * gateway nobody here configures. `/?page=audit` is one document at one path and needs nothing.
 */

export const PAGE_PARAMETER = "page";

/**
 * Where a merchant lands with nothing asked for.
 *
 * **Not the first page the rail offers, which is what every other console here does.** The rail's
 * first item is `Tenants`, and `Tenants` is blocked — as are the fourteen after it. A console whose
 * landing page is a readiness placeholder teaches a merchant in one second that it does not work,
 * and the design file's own initial state is `dlq`, which is blocked too, so the reference settles
 * nothing. So the landing page is the one page that draws from data.
 *
 * Derived rather than written: the first page with no `blockedBy`. When a listing is served and a
 * second page is drawn, this follows the rail's order again without being edited — and if every page
 * were blocked it would fall back to the rail's first, which is the honest answer to a console with
 * nothing in it. Recorded as an assumption in `docs/design/DESIGN_QUESTIONS.md` (Q-173) rather than
 * taken silently, because it is a departure from both the sibling consoles and the design file.
 */
export const DEFAULT_PAGE_ID: string =
  corporatePages.find((page) => page.blockedBy === undefined)?.id ??
  corporatePages[0]?.id ??
  "tenants";

const KNOWN_PAGES: ReadonlySet<string> = new Set(corporatePages.map((page) => page.id));

/**
 * Read the page out of a URL, ignoring anything that is not a page.
 *
 * An unknown identifier falls back to the default rather than rendering the shell's "No such page",
 * because the two mean different things to the merchant who sees them: a stale bookmark should open
 * the console, and a page the shell knows about but this merchant cannot reach should say so. The
 * second is the shell's job and it still happens.
 */
export function pageIdIn(url: string): string {
  const asked = new URL(url).searchParams.get(PAGE_PARAMETER);
  return asked !== null && KNOWN_PAGES.has(asked) ? asked : DEFAULT_PAGE_ID;
}

/**
 * The same URL, asking for a different page.
 *
 * Everything else in the address is preserved. That matters more than it looks: this is also the URL
 * the sign-in flow stores as its return address, so dropping a query parameter here would silently
 * drop it from what a merchant comes back to.
 */
export function urlForPage(url: string, pageId: string): string {
  const next = new URL(url);
  if (pageId === DEFAULT_PAGE_ID) next.searchParams.delete(PAGE_PARAMETER);
  else next.searchParams.set(PAGE_PARAMETER, pageId);
  return next.toString();
}
